import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { HERO } from "../../lib/content";

const EASE = [0.16, 1, 0.3, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yContent = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-20"
      data-testid="hero-section"
    >
      {/* ambient orbs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="bf-orb absolute -top-32 -left-24 h-[38rem] w-[38rem] rounded-full bg-bf-yellow/20 blur-[120px]" />
        <div className="bf-orb bf-orb-2 absolute top-1/3 -right-32 h-[34rem] w-[34rem] rounded-full bg-bf-teal/20 blur-[130px]" />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.6) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.6) 1px,transparent 1px)",
            backgroundSize: "64px 64px",
            maskImage: "radial-gradient(circle at 50% 40%, black, transparent 70%)"
          }}
        />
      </div>

      <motion.div style={{ y: yContent, opacity }} className="relative mx-auto max-w-7xl px-6 lg:px-10 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 mb-8"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-bf-yellow animate-pulse" />
          <span className="text-xs font-bold uppercase tracking-[0.22em] text-white/80">
            {HERO.overline}
          </span>
        </motion.div>

        <h1 className="font-display text-5xl md:text-7xl lg:text-[5.5rem] font-extrabold leading-[0.92] tracking-tighter max-w-5xl">
          {HERO.lines.map((line, i) => (
            <span key={i} className="bf-mask-line">
              <motion.span
                className="inline-block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, ease: EASE, delay: 0.15 + i * 0.12 }}
              >
                {i === HERO.lines.length - 1 ? (
                  <>
                    por <span className="text-bf-yellow">você.</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.6 }}
          className="mt-8 max-w-xl text-base md:text-lg text-white/65 leading-relaxed"
        >
          {HERO.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.75 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#contato"
            className="group inline-flex items-center gap-2 rounded-full bg-bf-yellow px-7 py-4 font-bold text-black transition-transform duration-200 hover:scale-[1.03]"
            data-testid="hero-cta-primary"
          >
            Entender como funciona
            <ArrowUpRight size={18} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href="#metodo"
            className="inline-flex items-center rounded-full border border-white/20 px-7 py-4 font-medium text-white hover:border-white/60 transition-colors duration-200"
            data-testid="hero-cta-secondary"
          >
            Ver o método
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="mt-16 grid grid-cols-3 gap-6 max-w-2xl border-t border-white/10 pt-8"
        >
          {HERO.stats.map((s) => (
            <div key={s.label}>
              <div className="font-display text-3xl md:text-4xl font-extrabold text-white">{s.value}</div>
              <div className="mt-1 text-xs md:text-sm text-white/50">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
